import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserSubroles } from '../hooks/useUserSubroles';
import { Shield, CheckCircle, XCircle, Filter, Trash2, Edit2 } from 'lucide-react';
import { Upload, Eye, Plus, Search, FileText, GripVertical, Download } from '../components/icons';
import SubirImagenEvidencia from '../components/SubirImagenEvidencia';
import AprobarEvidencia from '../components/AprobarEvidencia';
import GenerarPDFEvidencia from '../components/GenerarPDFEvidencia';
import EvidenciaSeguridadImage from '../components/EvidenciaSeguridadImage';
import CrearEvidenciaModal from '../components/CrearEvidenciaModal';
import api from '../services/api';

export default function EvidenciasSeguridad() {
    const { user } = useAuth();
    const { hasSubrole } = useUserSubroles();
    const [evidencias, setEvidencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtros, setFiltros] = useState({
        estado: '',
        prioridad: '',
        tipo: '',
        busqueda: '',
        codigoImplementacion: ''
    });
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const [evidenciaSeleccionada, setEvidenciaSeleccionada] = useState(null);
    const [mostrarModal, setMostrarModal] = useState(false);
    const [mostrarModalSubida, setMostrarModalSubida] = useState(false);
    const [mostrarModalAprobacion, setMostrarModalAprobacion] = useState(false);
    const [mostrarModalPDF, setMostrarModalPDF] = useState(false);
    const [mostrarModalCrearEvidencia, setMostrarModalCrearEvidencia] = useState(false);
    const [ordenamiento, setOrdenamiento] = useState({ campo: 'codigo', direccion: 'asc' });
    const [editandoComentario, setEditandoComentario] = useState(null);
    const [comentarioEditado, setComentarioEditado] = useState('');
    const [draggedImage, setDraggedImage] = useState(null);
    const [dragOverImage, setDragOverImage] = useState(null);
    const [evidenciasSeleccionadas, setEvidenciasSeleccionadas] = useState([]);
    const [mostrarModalAprobacionLote, setMostrarModalAprobacionLote] = useState(false);
    const [descargandoZip, setDescargandoZip] = useState(false);
    const [generandoPDFs, setGenerandoPDFs] = useState(false);

    // Verificar permisos
    const puedeGestionar = user?.role === 'admin' || user?.role === 'soporte';
    const puedeAprobar = hasSubrole('informatica') || user?.role === 'admin';

    useEffect(() => {
        cargarEvidencias();
    }, []);

    const cargarEvidencias = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/EvidenciaSeguridad');
            setEvidencias(data);
        } catch (error) {
            console.error('Error al cargar evidencias:', error);
            setError('Error al cargar las evidencias');
        } finally {
            setLoading(false);
        }
    }, []);

    const cargarEvidenciaEspecifica = useCallback(async (evidenciaId) => {
        try {
            const { data: evidenciaActualizada } = await api.get(`/EvidenciaSeguridad/${evidenciaId}`);
            setEvidenciaSeleccionada(evidenciaActualizada);

            // También actualizar la evidencia en la lista principal
            setEvidencias(prevEvidencias =>
                prevEvidencias.map(e => e.id === evidenciaId ? evidenciaActualizada : e)
            );
        } catch (error) {
            console.error('Error al cargar evidencia específica:', error);
        }
    }, []);

    // Memoized: sólo recalcula cuando evidencias, filtros u ordenamiento cambian
    const evidenciasFiltradas = useMemo(() => {
        const filtradas = evidencias.filter(evidencia => {
            const cumpleEstado = !filtros.estado || evidencia.estado === filtros.estado;
            const cumplePrioridad = !filtros.prioridad || evidencia.prioridad === filtros.prioridad;
            const cumpleTipo = !filtros.tipo || evidencia.tipo === filtros.tipo;
            const cumpleBusqueda = !filtros.busqueda ||
                evidencia.codigoControl.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
                evidencia.descripcionControl.toLowerCase().includes(filtros.busqueda.toLowerCase());
            const cumpleCodigoImplementacion = !filtros.codigoImplementacion ||
                evidencia.listaVerificacion.toLowerCase().includes(filtros.codigoImplementacion.toLowerCase());

            return cumpleEstado && cumplePrioridad && cumpleTipo && cumpleBusqueda && cumpleCodigoImplementacion;
        });

        // Aplicar ordenamiento según la selección del usuario
        return [...filtradas].sort((a, b) => {
            let resultado = 0;

            if (ordenamiento.campo === 'codigo') {
                const parsearCodigo = (codigo) => {
                    const partes = codigo.split('.');
                    if (partes.length >= 3) {
                        const parte1 = parseInt(partes[0]) || 0;
                        const parte2 = parseInt(partes[1]) || 0;
                        const parte3 = parseInt(partes[2]) || 0;
                        return parte1 * 10000 + parte2 * 100 + parte3;
                    }
                    return 0;
                };

                const codigoA = parsearCodigo(a.codigoControl);
                const codigoB = parsearCodigo(b.codigoControl);
                resultado = ordenamiento.direccion === 'desc' ? codigoB - codigoA : codigoA - codigoB;

                // Si los códigos son iguales, ordenar por número de lista de verificación
                if (resultado === 0) {
                    const extraerNumeroLista = (lista) => {
                        const match = lista.match(/^(\d+)/);
                        return match ? parseInt(match[1]) : Number.MAX_SAFE_INTEGER;
                    };

                    const numeroA = extraerNumeroLista(a.listaVerificacion);
                    const numeroB = extraerNumeroLista(b.listaVerificacion);
                    resultado = numeroA - numeroB;
                }
            } else if (ordenamiento.campo === 'prioridad') {
                const prioridadOrder = { 'Crítica': 4, 'Alta': 3, 'Media': 2, 'Baja': 1 };
                const prioridadA = prioridadOrder[a.prioridad] || 0;
                const prioridadB = prioridadOrder[b.prioridad] || 0;
                resultado = ordenamiento.direccion === 'desc' ? prioridadB - prioridadA : prioridadA - prioridadB;
            } else if (ordenamiento.campo === 'estado') {
                resultado = ordenamiento.direccion === 'desc'
                    ? b.estado.localeCompare(a.estado)
                    : a.estado.localeCompare(b.estado);
            } else if (ordenamiento.campo === 'codigoImplementacion') {
                // Extraer el número del inicio de la descripción de implementación
                const extraerNumeroImplementacion = (descripcion) => {
                    if (!descripcion) return Number.MAX_SAFE_INTEGER;
                    const match = descripcion.match(/^(\d+)/);
                    return match ? parseInt(match[1]) : Number.MAX_SAFE_INTEGER;
                };

                const numeroA = extraerNumeroImplementacion(a.descripcionImplementacion);
                const numeroB = extraerNumeroImplementacion(b.descripcionImplementacion);
                resultado = ordenamiento.direccion === 'desc' ? numeroB - numeroA : numeroA - numeroB;

                // Si los números son iguales, ordenar por código de control
                if (resultado === 0) {
                    const parsearCodigo = (codigo) => {
                        const partes = codigo.split('.');
                        if (partes.length >= 3) {
                            const parte1 = parseInt(partes[0]) || 0;
                            const parte2 = parseInt(partes[1]) || 0;
                            const parte3 = parseInt(partes[2]) || 0;
                            return parte1 * 10000 + parte2 * 100 + parte3;
                        }
                        return 0;
                    };

                    const codigoA = parsearCodigo(a.codigoControl);
                    const codigoB = parsearCodigo(b.codigoControl);
                    resultado = codigoB - codigoA;
                }
            }

            return resultado;
        });
    }, [evidencias, filtros, ordenamiento]);

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'Pendiente': return 'bg-yellow-100 text-yellow-800';
            case 'En Revisión': return 'bg-blue-100 text-blue-800';
            case 'Aprobado': return 'bg-green-100 text-green-800';
            case 'Rechazado': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getPrioridadColor = (prioridad) => {
        switch (prioridad) {
            case 'Crítica': return 'bg-red-100 text-red-800';
            case 'Alta': return 'bg-orange-100 text-orange-800';
            case 'Media': return 'bg-yellow-100 text-yellow-800';
            case 'Baja': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const cambiarOrdenamiento = useCallback((campo) => {
        setOrdenamiento(prev => {
            if (prev.campo === campo) {
                return { campo, direccion: prev.direccion === 'asc' ? 'desc' : 'asc' };
            } else {
                return { campo, direccion: 'desc' };
            }
        });
    }, []);

    const abrirModal = useCallback((evidencia) => {
        setEvidenciaSeleccionada(evidencia);
        setMostrarModal(true);
    }, []);

    const cerrarModal = useCallback(() => {
        setEvidenciaSeleccionada(null);
        setMostrarModal(false);
    }, []);

    const abrirModalSubida = useCallback((evidencia) => {
        setEvidenciaSeleccionada(evidencia);
        setMostrarModalSubida(true);
    }, []);

    const cerrarModalSubida = useCallback(() => {
        setEvidenciaSeleccionada(null);
        setMostrarModalSubida(false);
    }, []);

    const abrirModalAprobacion = useCallback((evidencia) => {
        setEvidenciaSeleccionada(evidencia);
        setMostrarModalAprobacion(true);
    }, []);

    const cerrarModalAprobacion = useCallback(() => {
        setEvidenciaSeleccionada(null);
        setMostrarModalAprobacion(false);
    }, []);

    const manejarImagenSubida = useCallback(async () => {
        await cargarEvidencias();
        if (evidenciaSeleccionada) {
            await cargarEvidenciaEspecifica(evidenciaSeleccionada.id);
        }
        cerrarModalSubida();
    }, [cargarEvidencias, cargarEvidenciaEspecifica, evidenciaSeleccionada, cerrarModalSubida]);

    const manejarAprobacionCompletada = useCallback(async () => {
        await cargarEvidencias();
        if (evidenciaSeleccionada) {
            await cargarEvidenciaEspecifica(evidenciaSeleccionada.id);
        }
        cerrarModalAprobacion();
    }, [cargarEvidencias, cargarEvidenciaEspecifica, evidenciaSeleccionada, cerrarModalAprobacion]);

    const abrirModalPDF = useCallback((evidencia) => {
        setEvidenciaSeleccionada(evidencia);
        setMostrarModalPDF(true);
    }, []);

    const cerrarModalPDF = useCallback(() => {
        setEvidenciaSeleccionada(null);
        setMostrarModalPDF(false);
    }, []);

    const abrirModalCrearEvidencia = useCallback(() => {
        setMostrarModalCrearEvidencia(true);
    }, []);

    const cerrarModalCrearEvidencia = useCallback(() => {
        setMostrarModalCrearEvidencia(false);
    }, []);

    const manejarEvidenciaCreada = useCallback(async () => {
        await cargarEvidencias();
        cerrarModalCrearEvidencia();
    }, [cargarEvidencias, cerrarModalCrearEvidencia]);

    // Funciones para selección múltiple
    const manejarSeleccionEvidencia = useCallback((evidenciaId) => {
        setEvidencias(prev => {
            const evidencia = prev.find(e => e.id === evidenciaId);
            if (evidencia && evidencia.estado !== 'En Revisión') return prev;
            return prev; // state unchanged; selection is separate
        });
        setEvidenciasSeleccionadas(prev => {
            const evidencia = evidencias.find(e => e.id === evidenciaId);
            if (evidencia && evidencia.estado !== 'En Revisión') return prev;
            return prev.includes(evidenciaId)
                ? prev.filter(id => id !== evidenciaId)
                : [...prev, evidenciaId];
        });
    }, [evidencias]);

    const seleccionarTodas = useCallback(() => {
        const idsDisponibles = evidenciasFiltradas
            .filter(e => e.estado === 'En Revisión')
            .map(e => e.id);
        setEvidenciasSeleccionadas(idsDisponibles);
    }, [evidenciasFiltradas]);

    const deseleccionarTodas = useCallback(() => {
        setEvidenciasSeleccionadas([]);
    }, []);

    const abrirModalAprobacionLote = useCallback(() => {
        if (evidenciasSeleccionadas.length === 0) {
            alert('Selecciona al menos una evidencia para aprobar');
            return;
        }
        setMostrarModalAprobacionLote(true);
    }, [evidenciasSeleccionadas.length]);

    const cerrarModalAprobacionLote = useCallback(() => {
        setMostrarModalAprobacionLote(false);
    }, []);

    const aprobarEvidenciasLote = useCallback(async () => {
        try {
            // Aprobar cada evidencia seleccionada
            for (const evidenciaId of evidenciasSeleccionadas) {
                await api.post(`/EvidenciaSeguridad/${evidenciaId}/aprobar`, {
                    aprobado: true,
                    comentariosAprobacion: `Aprobado en lote el ${new Date().toLocaleDateString()}`
                });
            }

            // Guardar la cantidad antes de limpiar
            const cantidadAprobadas = evidenciasSeleccionadas.length;

            // Recargar evidencias y limpiar selección
            await cargarEvidencias();
            setEvidenciasSeleccionadas([]);
            cerrarModalAprobacionLote();

            alert(`${cantidadAprobadas} evidencias aprobadas exitosamente`);
        } catch (error) {
            console.error('Error al aprobar evidencias en lote:', error);
            alert('Error al aprobar las evidencias');
        }
    }, [evidenciasSeleccionadas, cargarEvidencias, cerrarModalAprobacionLote]);

    const descargarZipEvidenciasAprobadas = useCallback(async () => {
        try {
            setDescargandoZip(true);
            const response = await api.get('/EvidenciaSeguridad/descargar-zip-aprobadas', {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `evidencias-aprobadas-${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            console.error('Error al descargar ZIP:', error);
            alert('Error al descargar el archivo ZIP');
        } finally {
            setDescargandoZip(false);
        }
    }, []);

    const generarPDFsEvidenciasAprobadas = useCallback(async () => {
        try {
            setGenerandoPDFs(true);
            const { data: resultado } = await api.post('/EvidenciaSeguridad/generar-pdfs-aprobadas');

            // Mostrar mensaje con los resultados
            if (resultado.errores && resultado.errores.length > 0) {
                alert(`PDFs generados: ${resultado.pdfsGenerados} de ${resultado.totalEvidencias}\n\nErrores:\n${resultado.errores.join('\n')}`);
            } else {
                alert(`PDFs generados exitosamente: ${resultado.pdfsGenerados} de ${resultado.totalEvidencias} evidencias aprobadas`);
            }

            // Recargar evidencias para actualizar las versiones
            await cargarEvidencias();

        } catch (error) {
            console.error('Error al generar PDFs:', error);
            alert('Error al generar los PDFs');
        } finally {
            setGenerandoPDFs(false);
        }
    }, [cargarEvidencias]);

    const eliminarImagen = useCallback(async (evidenciaId, imagenId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar esta imagen?')) {
            return;
        }

        try {
            await api.delete(`/EvidenciaSeguridad/${evidenciaId}/imagen/${imagenId}`);

            // Recargar las evidencias para actualizar la lista
            await cargarEvidencias();

            // Actualizar la evidencia seleccionada en el modal si está abierto
            if (evidenciaSeleccionada && evidenciaSeleccionada.id === evidenciaId) {
                // Filtrar la imagen eliminada del estado actual
                const imagenesActualizadas = evidenciaSeleccionada.imagenes.filter(img => img.id !== imagenId);
                setEvidenciaSeleccionada({
                    ...evidenciaSeleccionada,
                    imagenes: imagenesActualizadas
                });
            }
        } catch (error) {
            console.error('Error al eliminar imagen:', error);
            alert('Error al eliminar la imagen');
        }
    }, [cargarEvidencias, evidenciaSeleccionada]);

    const iniciarEdicionComentario = useCallback((imagenId, comentarioActual) => {
        setEditandoComentario(imagenId);
        setComentarioEditado(comentarioActual || '');
    }, []);

    const cancelarEdicionComentario = useCallback(() => {
        setEditandoComentario(null);
        setComentarioEditado('');
    }, []);

    const guardarComentario = useCallback(async (evidenciaId, imagenId) => {
        try {
            await api.put(`/EvidenciaSeguridad/${evidenciaId}/imagen/${imagenId}/comentario`, comentarioEditado);

            // Actualizar la evidencia seleccionada directamente con los datos más recientes
            if (evidenciaSeleccionada && evidenciaSeleccionada.id === evidenciaId) {
                await cargarEvidenciaEspecifica(evidenciaId);
            }

            // Actualizar la lista de evidencias después
            await cargarEvidencias();

            setEditandoComentario(null);
            setComentarioEditado('');
        } catch (error) {
            console.error('Error al actualizar comentario:', error);
            alert('Error al actualizar el comentario');
        }
    }, [cargarEvidencias, cargarEvidenciaEspecifica, comentarioEditado, evidenciaSeleccionada]);

    // Funciones para drag and drop
    const handleDragStart = (e, imagenId) => {
        setDraggedImage(imagenId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, imagenId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverImage(imagenId);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOverImage(null);
    };

    const handleDrop = async (e, targetImagenId) => {
        e.preventDefault();

        if (!draggedImage || draggedImage === targetImagenId) {
            setDraggedImage(null);
            setDragOverImage(null);
            return;
        }

        try {
            // Encontrar las posiciones de las imágenes
            const imagenes = [...evidenciaSeleccionada.imagenes];
            const draggedIndex = imagenes.findIndex(img => img.id === draggedImage);
            const targetIndex = imagenes.findIndex(img => img.id === targetImagenId);

            if (draggedIndex === -1 || targetIndex === -1) return;

            // Reordenar las imágenes
            const [draggedImg] = imagenes.splice(draggedIndex, 1);
            imagenes.splice(targetIndex, 0, draggedImg);

            // Actualizar el orden en la base de datos
            const response = await api.put(`/EvidenciaSeguridad/${evidenciaSeleccionada.id}/reordenar-imagenes`, {
                imagenIds: imagenes.map(img => img.id)
            });

            if (response.status === 200) {
                // Actualizar el estado local
                const evidenciaActualizada = {
                    ...evidenciaSeleccionada,
                    imagenes: imagenes
                };
                setEvidenciaSeleccionada(evidenciaActualizada);

                // Actualizar la lista principal
                await cargarEvidencias();
            } else {
                console.error('Error al reordenar imágenes');
                alert('Error al reordenar las imágenes');
            }
        } catch (error) {
            console.error('Error al reordenar imágenes:', error);
            alert('Error al reordenar las imágenes');
        } finally {
            setDraggedImage(null);
            setDragOverImage(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando evidencias...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={cargarEvidencias}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Shield className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Evidencias de Seguridad</h1>
                            <p className="text-gray-600 dark:text-gray-300">Gestión de evidencias de cumplimiento de controles de seguridad</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        {puedeAprobar && (
                            <>
                                <button
                                    onClick={generarPDFsEvidenciasAprobadas}
                                    disabled={generandoPDFs}
                                    className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    <FileText className="h-4 w-4" />
                                    <span>{generandoPDFs ? 'Generando...' : 'Generar PDFs'}</span>
                                </button>
                                <button
                                    onClick={descargarZipEvidenciasAprobadas}
                                    disabled={descargandoZip}
                                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    <Download className="h-4 w-4" />
                                    <span>{descargandoZip ? 'Generando...' : 'Descargar ZIP'}</span>
                                </button>
                                <button
                                    onClick={abrirModalAprobacionLote}
                                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    disabled={evidenciasSeleccionadas.length === 0}
                                >
                                    <CheckCircle className="h-4 w-4" />
                                    <span>Aprobar ({evidenciasSeleccionadas.length})</span>
                                </button>
                            </>
                        )}
                        {puedeGestionar && (
                            <button
                                onClick={abrirModalCrearEvidencia}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Nueva Evidencia</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros</h3>
                    <button
                        onClick={() => setMostrarFiltros(!mostrarFiltros)}
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
                    >
                        <Filter className="h-4 w-4" />
                        <span>{mostrarFiltros ? 'Ocultar' : 'Mostrar'} Filtros</span>
                    </button>
                </div>

                {mostrarFiltros && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estado</label>
                            <select
                                value={filtros.estado}
                                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todos los estados</option>
                                <option value="Pendiente">Pendiente</option>
                                <option value="En Revisión">En Revisión</option>
                                <option value="Aprobado">Aprobado</option>
                                <option value="Rechazado">Rechazado</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prioridad</label>
                            <select
                                value={filtros.prioridad}
                                onChange={(e) => setFiltros({ ...filtros, prioridad: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todas las prioridades</option>
                                <option value="Crítica">Crítica</option>
                                <option value="Alta">Alta</option>
                                <option value="Media">Media</option>
                                <option value="Baja">Baja</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo</label>
                            <select
                                value={filtros.tipo}
                                onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todos los tipos</option>
                                <option value="Implementación">Implementación</option>
                                <option value="Revisión Periódica">Revisión Periódica</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Código Implementación</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                                <input
                                    type="text"
                                    value={filtros.codigoImplementacion}
                                    onChange={(e) => setFiltros({ ...filtros, codigoImplementacion: e.target.value })}
                                    placeholder="Ej: MFA, ONETRUST..."
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Búsqueda General</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                                <input
                                    type="text"
                                    value={filtros.busqueda}
                                    onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                                    placeholder="Buscar por código o descripción..."
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Ordenamiento */}
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ordenar por:</h3>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => cambiarOrdenamiento('codigo')}
                        className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${ordenamiento.campo === 'codigo'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                    >
                        <span>Código de Control</span>
                        {ordenamiento.campo === 'codigo' && (
                            <span className="text-xs">
                                {ordenamiento.direccion === 'desc' ? '↓' : '↑'}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => cambiarOrdenamiento('prioridad')}
                        className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${ordenamiento.campo === 'prioridad'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                    >
                        <span>Prioridad</span>
                        {ordenamiento.campo === 'prioridad' && (
                            <span className="text-xs">
                                {ordenamiento.direccion === 'desc' ? '↓' : '↑'}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => cambiarOrdenamiento('estado')}
                        className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${ordenamiento.campo === 'estado'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                    >
                        <span>Estado</span>
                        {ordenamiento.campo === 'estado' && (
                            <span className="text-xs">
                                {ordenamiento.direccion === 'desc' ? '↓' : '↑'}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => cambiarOrdenamiento('codigoImplementacion')}
                        className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${ordenamiento.campo === 'codigoImplementacion'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                    >
                        <span>Código Implementación</span>
                        {ordenamiento.campo === 'codigoImplementacion' && (
                            <span className="text-xs">
                                {ordenamiento.direccion === 'desc' ? '↓' : '↑'}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Lista de Evidencias */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Evidencias ({evidenciasFiltradas.length})
                        </h3>
                        {puedeAprobar && (
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={seleccionarTodas}
                                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                    Seleccionar Todas
                                </button>
                                <button
                                    onClick={deseleccionarTodas}
                                    className="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                >
                                    Deseleccionar Todas
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {evidenciasFiltradas.map((evidencia) => (
                        <div key={evidencia.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 flex-1">
                                    {puedeAprobar && (
                                        <input
                                            type="checkbox"
                                            checked={evidenciasSeleccionadas.includes(evidencia.id)}
                                            onChange={() => manejarSeleccionEvidencia(evidencia.id)}
                                            disabled={evidencia.estado !== 'En Revisión'}
                                            className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${evidencia.estado !== 'En Revisión'
                                                ? 'opacity-50 cursor-not-allowed'
                                                : 'cursor-pointer'
                                                }`}
                                        />
                                    )}
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {evidencia.codigoControl}
                                            </h4>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(evidencia.estado)}`}>
                                                {evidencia.estado}
                                            </span>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPrioridadColor(evidencia.prioridad)}`}>
                                                {evidencia.prioridad}
                                            </span>
                                        </div>

                                        <p className="text-gray-600 dark:text-gray-300 mb-1">{evidencia.descripcionControl}</p>
                                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">{evidencia.descripcionImplementacion || 'Sin descripción de implementación'}</p>

                                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                            <span>Tipo: {evidencia.tipo}</span>
                                            <span>Intervalo: {evidencia.intervaloRevision}</span>
                                            <span>Imágenes: {evidencia.imagenes?.length || 0}</span>
                                            {evidencia.fechaAprobacion && (
                                                <span>Aprobado: {new Date(evidencia.fechaAprobacion).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                    {puedeGestionar && (
                                        <button
                                            onClick={() => abrirModalSubida(evidencia)}
                                            className="flex items-center space-x-1 px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                        >
                                            <Upload className="h-4 w-4" />
                                            <span>Subir</span>
                                        </button>
                                    )}

                                    <button
                                        onClick={() => abrirModal(evidencia)}
                                        className="flex items-center space-x-1 px-3 py-1 text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                                    >
                                        <Eye className="h-4 w-4" />
                                        <span>Ver</span>
                                    </button>

                                    <button
                                        onClick={() => abrirModalPDF(evidencia)}
                                        className="flex items-center space-x-1 px-3 py-1 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
                                    >
                                        <FileText className="h-4 w-4" />
                                        <span>PDF</span>
                                    </button>

                                    {puedeAprobar && evidencia.estado === 'En Revisión' && (
                                        <button
                                            onClick={() => abrirModalAprobacion(evidencia)}
                                            className="flex items-center space-x-1 px-3 py-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                        >
                                            <CheckCircle className="h-4 w-4" />
                                            <span>Aprobar</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {evidenciasFiltradas.length === 0 && (
                    <div className="text-center py-12">
                        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No se encontraron evidencias con los filtros aplicados</p>
                    </div>
                )}
            </div>

            {/* Modal de Detalle */}
            {mostrarModal && evidenciaSeleccionada && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            cerrarModal();
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {evidenciaSeleccionada.descripcionControl}
                                </h3>
                                <button
                                    onClick={cerrarModal}
                                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                                >
                                    <XCircle className="h-6 w-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Información del Control</h4>
                                    <dl className="space-y-2">
                                        <div>
                                            <dt className="font-medium text-gray-700 dark:text-gray-300">Descripción del Control:</dt>
                                            <dd className="text-gray-600 dark:text-gray-400">{evidenciaSeleccionada.descripcionImplementacion || 'Sin descripción'}</dd>
                                        </div>
                                        <div>
                                            <dt className="font-medium text-gray-700 dark:text-gray-300">Código de Implementación:</dt>
                                            <dd className="text-gray-600 dark:text-gray-400">{evidenciaSeleccionada.codigoControl}</dd>
                                        </div>
                                        <div>
                                            <dt className="font-medium text-gray-700 dark:text-gray-300">Prioridad:</dt>
                                            <dd className="text-gray-600 dark:text-gray-400">{evidenciaSeleccionada.prioridad}</dd>
                                        </div>
                                        <div>
                                            <dt className="font-medium text-gray-700 dark:text-gray-300">Tipo:</dt>
                                            <dd className="text-gray-600 dark:text-gray-400">{evidenciaSeleccionada.tipo}</dd>
                                        </div>
                                        <div>
                                            <dt className="font-medium text-gray-700 dark:text-gray-300">Intervalo de Revisión:</dt>
                                            <dd className="text-gray-600 dark:text-gray-400">{evidenciaSeleccionada.intervaloRevision}</dd>
                                        </div>
                                    </dl>
                                </div>

                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Estado y Evidencias</h4>
                                    <div className="space-y-4">
                                        <div>
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEstadoColor(evidenciaSeleccionada.estado)}`}>
                                                {evidenciaSeleccionada.estado}
                                            </span>
                                        </div>

                                        {evidenciaSeleccionada.comentarios && (
                                            <div>
                                                <dt className="font-medium text-gray-700 dark:text-gray-300">Comentarios:</dt>
                                                <dd className="text-gray-600 dark:text-gray-400">{evidenciaSeleccionada.comentarios}</dd>
                                            </div>
                                        )}

                                        {evidenciaSeleccionada.comentariosAprobacion && (
                                            <div>
                                                <dt className="font-medium text-gray-700 dark:text-gray-300">
                                                    {evidenciaSeleccionada.estado === 'Rechazado' ? 'Comentarios de Rechazo:' :
                                                        evidenciaSeleccionada.estado === 'Aprobado' ? 'Comentarios de Aprobación:' :
                                                            'Comentarios:'}
                                                </dt>
                                                <dd className="text-gray-600 dark:text-gray-400">{evidenciaSeleccionada.comentariosAprobacion}</dd>
                                            </div>
                                        )}

                                        <div>
                                            <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Imágenes ({evidenciaSeleccionada.imagenes?.length || 0})</h5>
                                            {evidenciaSeleccionada.imagenes && evidenciaSeleccionada.imagenes.length > 0 ? (
                                                <div className="grid grid-cols-2 gap-2">
                                                    {evidenciaSeleccionada.imagenes.map((imagen, index) => (
                                                        <div
                                                            key={imagen.id}
                                                            className={`relative border border-gray-200 dark:border-gray-600 rounded-lg p-2 cursor-move transition-all duration-200 ${draggedImage === imagen.id
                                                                ? 'opacity-50 scale-95'
                                                                : dragOverImage === imagen.id
                                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                                    : 'hover:border-gray-300 dark:hover:border-gray-500'
                                                                }`}
                                                            draggable={puedeGestionar}
                                                            onDragStart={(e) => handleDragStart(e, imagen.id)}
                                                            onDragOver={(e) => handleDragOver(e, imagen.id)}
                                                            onDragLeave={handleDragLeave}
                                                            onDrop={(e) => handleDrop(e, imagen.id)}
                                                        >
                                                            <EvidenciaSeguridadImage
                                                                previewUrl={imagen.previewUrl}
                                                                alt={imagen.descripcion || `Imagen ${index + 1}`}
                                                                className="w-full h-24 object-cover rounded"
                                                            />

                                                            {/* Indicador de arrastre */}
                                                            {puedeGestionar && (
                                                                <div className="absolute top-1 left-1 p-1 bg-gray-800 bg-opacity-75 text-white rounded cursor-move">
                                                                    <GripVertical className="h-3 w-3" />
                                                                </div>
                                                            )}

                                                            {/* Comentario editable */}
                                                            <div className="mt-1">
                                                                {editandoComentario === imagen.id ? (
                                                                    <div className="space-y-2">
                                                                        <textarea
                                                                            value={comentarioEditado}
                                                                            onChange={(e) => setComentarioEditado(e.target.value)}
                                                                            placeholder="Agrega un comentario para esta imagen..."
                                                                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-800 dark:text-white resize-none"
                                                                            rows="2"
                                                                            autoFocus
                                                                        />
                                                                        <div className="flex justify-end space-x-1">
                                                                            <button
                                                                                onClick={cancelarEdicionComentario}
                                                                                className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                                                                            >
                                                                                Cancelar
                                                                            </button>
                                                                            <button
                                                                                onClick={() => guardarComentario(evidenciaSeleccionada.id, imagen.id)}
                                                                                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                                            >
                                                                                Guardar
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 py-1 transition-colors text-center"
                                                                        onClick={() => iniciarEdicionComentario(imagen.id, imagen.descripcion)}
                                                                        title="Haz clic para editar el comentario"
                                                                    >
                                                                        <div className="text-xs font-medium text-center">
                                                                            {imagen.descripcion || `Imagen ${index + 1}`}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Botones de acción */}
                                                            {puedeGestionar && (
                                                                <div className="absolute top-1 right-1 flex space-x-1">
                                                                    <button
                                                                        onClick={() => iniciarEdicionComentario(imagen.id, imagen.descripcion)}
                                                                        className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
                                                                        title="Editar comentario"
                                                                    >
                                                                        <Edit2 className="h-3 w-3" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => eliminarImagen(evidenciaSeleccionada.id, imagen.id)}
                                                                        className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                                                        title="Eliminar imagen"
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 dark:text-gray-400 text-sm">No hay imágenes subidas</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de Subida de Imágenes */}
            {mostrarModalSubida && evidenciaSeleccionada && (
                <SubirImagenEvidencia
                    evidenciaId={evidenciaSeleccionada.id}
                    onImagenSubida={manejarImagenSubida}
                    onClose={cerrarModalSubida}
                    isOpen={mostrarModalSubida}
                />
            )}

            {/* Modal de Aprobación */}
            {mostrarModalAprobacion && evidenciaSeleccionada && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            cerrarModalAprobacion();
                        }
                    }}
                >
                    <AprobarEvidencia
                        evidencia={evidenciaSeleccionada}
                        onAprobacionCompletada={manejarAprobacionCompletada}
                        onCancelar={cerrarModalAprobacion}
                    />
                </div>,
                document.body
            )}

            {/* Modal de Aprobación en Lote */}
            {mostrarModalAprobacionLote && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            cerrarModalAprobacionLote();
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Aprobar Evidencias en Lote
                            </h3>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                ¿Estás seguro de que quieres aprobar las {evidenciasSeleccionadas.length} evidencias seleccionadas?
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={cerrarModalAprobacionLote}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={aprobarEvidenciasLote}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    Aprobar Todas
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de Generar PDF */}
            {mostrarModalPDF && evidenciaSeleccionada && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            cerrarModalPDF();
                        }
                    }}
                >
                    <GenerarPDFEvidencia
                        evidencia={evidenciaSeleccionada}
                        onCancelar={cerrarModalPDF}
                    />
                </div>,
                document.body
            )}

            {/* Modal de Crear Evidencia */}
            <CrearEvidenciaModal
                isOpen={mostrarModalCrearEvidencia}
                onClose={cerrarModalCrearEvidencia}
                onEvidenciaCreada={manejarEvidenciaCreada}
            />
        </div>
    );
}
